import { useEffect, useMemo, useState } from 'react';
import { useFormik, validateYupSchema, yupToFormErrors } from 'formik';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { Button } from '@/components/Button/Button';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { isValidForm } from '@/helpers/formHelpers/formHelpers';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { VaultRegionType } from '@/types/myVaultTypes';
import { CreateVaultAccountPayloadType } from '@/api/v1/organization/my-vault/createVaultAccountApi';
import { createVaultAccount, loadMyVaultCatalog, MY_VAULT_QUERY_KEYS } from '../../api/api';
import { RailFields } from '../RailFields/RailFields';
import { OptionCard, OptionGrid } from '../../styles';
import {
  CREATE_VAULT_ACCOUNT_INITIAL_VALUES,
  CreateVaultAccountFormType,
  getCreateVaultAccountSchema,
  neededFields,
  OTHER_BANK,
  STEPS,
} from './const/const';
import { Col, Grid, StepBar, StepLabel } from './styles';
import { InputError } from '@/components/form/fields/InputError/InputError';
import { FormikChips } from '@/components/form/fields/FormikChips/FormikChips';
import SuccessIcon from '@/images/SuccessSecondaryIcon.svg?react';
import { COLORS } from '@/styles/colors';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';

type CreateVaultAccountModalType = {
  onClose: () => void;
};

export const CreateVaultAccountModal = ({ onClose }: CreateVaultAccountModalType) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [savedRegion, setSavedRegion] = useState<VaultRegionType | null>(null);

  const { data: catalogResponse } = useQuery({
    queryKey: MY_VAULT_QUERY_KEYS.catalog,
    queryFn: loadMyVaultCatalog,
    staleTime: Infinity,
  });
  const catalog = catalogResponse?.body;

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['create_vault_account'],
    mutationFn: (payload: CreateVaultAccountPayloadType) => createVaultAccount(payload),
    onSuccess(response, payload) {
      if (response?.error) return showSnackbarResponseError(response.error);

      amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateVaultAccountSuccess);
      queryClient.invalidateQueries({ queryKey: [MY_VAULT_QUERY_KEYS.accounts] });
      queryClient.invalidateQueries({ queryKey: MY_VAULT_QUERY_KEYS.regions });
      setSavedRegion(payload.region);
      setStep(5);
    },
  });

  const formik = useFormik<CreateVaultAccountFormType>({
    initialValues: CREATE_VAULT_ACCOUNT_INITIAL_VALUES,
    enableReinitialize: true,
    validate: async (values) => {
      if (!catalog) return {};

      const schema = getCreateVaultAccountSchema(step, catalog.regions[values.region], values.rails);

      try {
        await validateYupSchema(values, schema);

        return {};
      } catch (error) {
        return yupToFormErrors(error);
      }
    },
    onSubmit: (values) => {
      const meta = catalog.regions[values.region];
      const payload: CreateVaultAccountPayloadType = {
        region: values.region,
        bank_name: values.bank === OTHER_BANK ? values.otherBank.trim() : values.bank,
        bank_is_other: values.bank === OTHER_BANK,
        nickname: values.nickname.trim(),
        holder_type: values.holder_type,
        rails: values.rails,
        fields: Object.fromEntries(
          neededFields(meta, values.rails).map((field) => [field.key, (values.fields[field.key] ?? '').trim()])
        ),
      };

      mutate(payload);
    },
  });

  const meta = catalog?.regions[formik.values.region];
  const fields = useMemo(() => (meta ? neededFields(meta, formik.values.rails) : []), [meta, formik.values.rails]);
  const regions = catalog ? (Object.keys(catalog.regions) as VaultRegionType[]) : [];

  useEffect(() => {
    if (step === 4) {
      fields.forEach((f) => {
        formik.setFieldValue(`fields.${f.key}`, '');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, fields]);

  const next = async () => {
    if (step < 4) {
      const isValid = await isValidForm(formik);

      if (!isValid) return;

      setStep(step + 1);
      formik.setTouched({});

      return;
    }

    formik.handleSubmit();
  };

  const selectRegion = (region: VaultRegionType) => {
    formik.resetForm({ values: { ...CREATE_VAULT_ACCOUNT_INITIAL_VALUES, region } });
  };

  const toggleRail = (code: string) => {
    const { rails } = formik.values;

    formik.setFieldValue('rails', rails.includes(code) ? rails.filter((r) => r !== code) : [...rails, code]);
  };

  const startAnother = (region: VaultRegionType) => {
    formik.resetForm({ values: { ...CREATE_VAULT_ACCOUNT_INITIAL_VALUES, region } });
    setSavedRegion(null);
    setStep(2);
  };

  if (!catalog) return <ContentLayout title='Set up My Vault' />;

  return (
    <div>
      <Grid $cols={5} $gap={8}>
        {STEPS.map((s, i) => (
          <Col key={s} $gap={6}>
            <StepBar $done={i + 1 <= step} />
            <StepLabel $done={i + 1 <= step}>{s}</StepLabel>
          </Col>
        ))}
      </Grid>
      <br />

      <FormikForm value={formik}>
        {step === 1 && (
          <Box display='flex' flexDirection='column' rowGap='12px'>
            <Typography fontWeight='semibold'>Where is this account held?</Typography>
            <OptionGrid>
              {regions.map((region) => (
                <OptionCard
                  key={region}
                  type='button'
                  $active={formik.values.region === region}
                  onClick={() => selectRegion(region)}
                >
                  <Typography fontWeight='semibold'>{catalog.regions[region].label}</Typography>
                  <Typography variant='bodySmall'>
                    {catalog.regions[region].currency} · {catalog.regions[region].rails.map((r) => r.label).join(', ')}
                  </Typography>
                </OptionCard>
              ))}
            </OptionGrid>
          </Box>
        )}

        {step === 2 && (
          <Box display='flex' flexDirection='column' rowGap='12px'>
            <Typography fontWeight='semibold'>Choose your bank</Typography>
            <OptionGrid $cols={3}>
              {[...meta.banks, OTHER_BANK].map((bank) => (
                <OptionCard
                  key={bank}
                  type='button'
                  $active={formik.values.bank === bank}
                  onClick={() => formik.setFieldValue('bank', bank)}
                >
                  <Typography fontWeight='semibold'>{bank === OTHER_BANK ? 'Other / enter manually' : bank}</Typography>
                </OptionCard>
              ))}
            </OptionGrid>
            {formik.touched.bank && formik.errors.bank && (
              <InputError error={formik.errors.bank as string}></InputError>
            )}
            {formik.values.bank === OTHER_BANK && (
              <FormikInput
                name='otherBank'
                label='Bank name*'
                placeholder="Type your bank's name"
                autoComplete='new-field'
              />
            )}
          </Box>
        )}

        {step === 3 && (
          <Box display='flex' flexDirection='column' rowGap='12px'>
            <Typography fontWeight='semibold'>
              Will you be sharing banking information to receive the following transfers?
            </Typography>
            <Typography variant='bodySmall'>Pick one or more. You can add the others later.</Typography>
            <OptionGrid>
              {meta.rails.map((rail) => (
                <OptionCard
                  key={rail.code}
                  type='button'
                  $active={formik.values.rails.includes(rail.code)}
                  onClick={() => toggleRail(rail.code)}
                >
                  <Typography fontWeight='semibold'>{rail.label}</Typography>
                  <Typography variant='bodySmall'>{rail.hint}</Typography>
                </OptionCard>
              ))}
              {meta.rails.length > 2 && (
                <OptionCard
                  type='button'
                  $active={formik.values.rails.length === meta.rails.length}
                  onClick={() =>
                    formik.setFieldValue(
                      'rails',
                      meta.rails.map((r) => r.code)
                    )
                  }
                >
                  <Typography fontWeight='semibold'>All of the above</Typography>
                  <Typography variant='bodySmall'>Collect every field in one go</Typography>
                </OptionCard>
              )}
            </OptionGrid>
            {formik.touched.rails && typeof formik.errors.rails === 'string' && (
              <InputError error={formik.errors.rails}></InputError>
            )}
          </Box>
        )}

        {step === 4 && (
          <Box display='flex' flexDirection='column'>
            <FormikChips
              label='Account holder'
              name='holder_type'
              options={[
                { value: 'business', label: 'Business' },
                { value: 'individual', label: 'Individual' },
              ]}
            />

            <FormikInput
              name='nickname'
              label='Account nickname*'
              placeholder='e.g. Chase Operating Account'
              autoComplete='new-field'
              apiError={data?.error}
            />
            <RailFields
              fields={fields}
              holderType={formik.values.holder_type}
              bankName={formik.values.bank === OTHER_BANK ? formik.values.otherBank : formik.values.bank}
              apiError={data?.error}
            />
            <Typography variant='bodySmall'>
              Numbers are encrypted at rest and masked everywhere after saving. Saving creates a tamper-evident record
              on the WireVault blockchain. Any later change creates a new version and notifies people you shared with.
            </Typography>
          </Box>
        )}

        {step === 5 && savedRegion && (
          <Box display='flex' flexDirection='column' rowGap='16px' alignItems='flex-start'>
            <Box display='flex' alignItems='center' gap={'18px'}>
              <SuccessIcon />
              <Typography variant='headline4'>Saved to {catalog?.regions[savedRegion]?.vault}</Typography>
            </Box>

            <Typography variant='bodySmall'>Confirm it with your second factor to mark it Confirmed.</Typography>
            <Box
              display='flex'
              background={COLORS.mint[50]}
              flexDirection='column'
              borderRadius={'12px'}
              padding={'14px'}
              gap={'14px'}
              width={'100%'}
              alignItems='flex-start'
            >
              <Typography fontWeight='semibold'>Do you want to add accounts in another country or region?</Typography>

              <Box display='flex' flexWrap='wrap' columnGap='10px' rowGap='10px' flexDirection='column'>
                {regions
                  .filter((region) => region !== savedRegion)
                  .map((region) => (
                    <ButtonNew key={region} variant='secondary' onClick={() => startAnother(region)}>
                      Yes, add {catalog.regions[region].label} account
                    </ButtonNew>
                  ))}
              </Box>

              <ButtonNew onClick={onClose}>No, finish setup</ButtonNew>
            </Box>
          </Box>
        )}
      </FormikForm>

      {step < 5 && (
        <Box display='flex' justifyContent='space-between' marginTop='36px'>
          <Button
            size='medium'
            variant='secondary'
            disabled={step === 1}
            onClick={() => setStep(step - 1)}
            width='auto'
          >
            Back
          </Button>
          <Button size='medium' isLoading={isPending} onClick={next} width='auto'>
            {step === 4 ? 'Save to My Vault' : 'Continue'}
          </Button>
        </Box>
      )}
    </div>
  );
};
